import type { TractorWithAxlesResponse } from '@contracts/tractor/TractorWithAxlesResponse';
import { VehicleAxlesList } from '@src/axles/domain/VehicleAxlesList';
import { TractorList } from '@src/tractor/domain/Tractor';
import { TractorClient } from '@src/tractor/services/TractorClient';
import type { TractorFormInputs } from '@src/tractor/TractorFormInputs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type * as yup from 'yup';

interface Props {
  clientId: string;
  onSuccess: (newTractor: TractorWithAxlesResponse) => void;
  onError: () => void;
}

export const useCreateTractor = ({ clientId, onSuccess, onError }: Props) => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (data: yup.InferType<typeof TractorFormInputs>) =>
      TractorClient.create(clientId, {
        unitNumber: data.tractorUnitNumber,
        make: data.tractorMake,
        model: data.tractorModelNumber,
        fuelType: data.tractorFuelType,
        bodyStyle: data.tractorBodyStyle,
        type: data.tractorType,
        vin: data.tractorVIN,
        year: data.tractorYear,
        specialDesign: data.vehicleOfSpecialDesign,
        licensePlate: data.tractorLicensePlateNumber,
        licensePlateState: data.tractorLicensePlateState,
        licensePlateEffectiveDate: data.tractorLicenseEffectiveDate,
        licensePlateExpirationDate: data.tractorLicenseExpirationDate,
        licensePlateType: data.tractorLicensePlateType,
        licensePlateYear: data.tractorLicensePlateYear,
        licensePlateIsApportioned: data.tractorLicenseIsApportionedPlate,
        length: data.tractorLength,
        width: data.tractorWidth,
        height: data.tractorHeight,
        wheelBase: data.tractorWheelBase,
        groundHeight: data.tractorGroundHeight,
        unladenWeight: data.tractorUnladenWeight,
        actualWeight: data.tractorActualWeight,
        gvw: data.tractorGVW,
        outerBridgeLength: data.tractorOuterBridgeLength,
        axles: data.tractorAxles.axles?.map(
          (axle: yup.InferType<typeof TractorFormInputs>['axles']) => ({
            weight: axle.axleWeight ?? undefined,
            capacity: axle.axleCapacity ?? undefined,
            width: axle.axleWidth ?? undefined,
            tireCount: axle.tireCount ?? undefined,
            tireRating: axle.tireRating ?? undefined,
            tireSize: axle.tireSize ?? undefined,
            suspensionType: axle.suspensionType ?? undefined,
            group: axle.axleGroup ?? undefined,
            isSteeringAxle: axle.isSteeringAxle ?? undefined,
            spacing: axle.axleSpacing ?? undefined,
            type: axle.axleType ?? undefined,
          }),
        ),
      }),
    onSuccess: async (newTractor) => {
      onSuccess(newTractor);
      queryClient.setQueryData(
        ['tractors.list', clientId],
        (prev: TractorList | undefined) => {
          if (!prev) {
            return new TractorList([newTractor]);
          }
          return new TractorList([...prev.tractors, newTractor]);
        },
      );
      queryClient.setQueryData(
        ['axles.list', clientId],
        (prev: VehicleAxlesList | undefined) => {
          if (!prev) {
            return new VehicleAxlesList({
              trailersAxles: [],
              tractorsAxles: [
                { vehicleId: newTractor.id, axles: newTractor.axles },
              ],
            });
          }
          return new VehicleAxlesList({
            trailersAxles: prev.trailersAxles,
            tractorsAxles: [
              { vehicleId: newTractor.id, axles: newTractor.axles },
            ],
          });
        },
      );
      await queryClient.invalidateQueries({
        queryKey: ['tractors.list', clientId],
      });
    },
    onError,
  });

  return {
    createTractor: mutate,
    isLoading: isPending,
  };
};
